/**
 * CALM DON — zwei Ergänzungen am Warenkorb: Empfehlungszeile und Toast.
 *
 * ⚠️ **Der Drawer wird bei jeder Korb-Änderung gemorpht.**
 * `snippets/cart-drawer.liquid:64` setzt `data-hydration-key="cart-drawer-inner"`
 * um den gesamten Drawer-Inhalt. `assets/morph.js:172-203` morpht innerhalb
 * eines solchen Schlüssels **vollständig** (`hydrationMode:false,
 * childrenOnly:false`), Attribute inklusive. Alles, was dieses Modul in den DOM
 * schreibt, ist danach weg — nicht weil es keinen Schlüssel hat, sondern weil
 * es in der Serverantwort nicht vorkommt.
 *
 * Deshalb: Die Hülle kommt aus Liquid (`cd-cart-upsell.liquid`) und wird
 * mitgerendert; dieses Modul füllt sie nur und hält keinen Zustand über einen
 * Morph hinweg.
 *
 * ⚠️ **Vier Fallen, jede davon hier einmal getreten und wieder ausgebaut:**
 *
 *   1. `source` steht NICHT am Ereignis, sondern im Auflösungswert des
 *      angehängten Promise. `ShopifyStandardEvent` erbt von `Event`, nicht von
 *      `CustomEvent` — `event.detail` ist `undefined`, jede Prüfung darauf
 *      immer falsch.
 *   2. `window.Theme` gibt es nicht. `snippets/scripts.liquid:287` deklariert
 *      `const Theme` — ein lexikalisches Binding ohne `window`-Eigenschaft.
 *      Route und Geldformat kommen deshalb als `data-`Attribut aus Liquid.
 *      (Und `Theme.routes.cart_add_url` trägt bereits `.js`, taugt also
 *      ohnehin nicht als Formular-`action`.)
 *   3. Ein modaler `<dialog>` liegt in der **Top Layer**. Kein `z-index`
 *      erreicht ihn. Der Toast braucht `popover`, sonst ist er unter 990 px
 *      unsichtbar — genau dort, wo `theme-drawer.js` `showModal()` benutzt.
 *   4. Beim ersten Artikel in einen leeren Korb morpht Horizon **asynchron**
 *      über `startViewTransition` (`component-cart-items.js:361-365`). Ein
 *      einzelnes `requestAnimationFrame` kommt dann zu früh.
 */

import { formatMoney } from '@theme/money-formatting';

const UPSELL = '[data-cd-upsell]';
const LIMIT = 6;

/** @type {AbortController | null} */
let pending = null;
/** @type {MutationObserver | null} */
let watcher = null;
let queued = false;

/* ---------------------------------------------------------------- Toast -- */

/** @type {HTMLElement | null} */
let toastEl = null;
let toastTimer = 0;

/**
 * Kurze Bestätigung nach dem Hinzufügen.
 *
 * Der Entwurf zeigt sie 2600 ms und öffnet den Korb NICHT — die Bestätigung
 * ersetzt das Aufspringen. Ob stattdessen aufgesprungen wird, entscheidet
 * Horizons Setting `auto_open_cart_drawer`, nicht dieses Modul.
 *
 * @param {string} text
 */
function toast(text) {
  if (!text) return;
  if (!toastEl) {
    toastEl = document.createElement('div');
    toastEl.className = 'cd-toast';
    // `status` statt `alert`: eine Bestätigung, keine Warnung — sie soll den
    // Screenreader nicht unterbrechen.
    toastEl.setAttribute('role', 'status');
    toastEl.setAttribute('aria-live', 'polite');
    // ⚠️ Falle 3. Ohne `popover` liegt der Toast hinter dem modalen Drawer.
    toastEl.popover = 'manual';
    document.body.appendChild(toastEl);
  }
  toastEl.textContent = text;
  try {
    toastEl.showPopover();
  } catch {
    // Browser ohne Popover: Der Toast liegt dann wieder unter dem modalen
    // Drawer, ausserhalb davon bleibt er sichtbar.
  }
  // ⚠️ Ein Frame Abstand ist nötig. `[popover]:not(:popover-open)` ist
  // `display: none`; wird die Klasse im selben Frame gesetzt, gibt es keinen
  // Zustand, von dem aus animiert werden könnte — der Toast springt.
  requestAnimationFrame(() => toastEl?.classList.add('is-visible'));
  clearTimeout(toastTimer);
  toastTimer = setTimeout(hideToast, 2600);
}

function hideToast() {
  if (!toastEl) return;
  const el = toastEl;
  el.classList.remove('is-visible');
  // Erst ausgleiten lassen, dann aus der Top Layer nehmen — sonst ist er
  // sofort weg statt sanft.
  setTimeout(() => {
    try {
      el.hidePopover();
    } catch {
      /* siehe oben */
    }
  }, 450);
}

/* --------------------------------------------------------------- Upsell -- */

/**
 * Holt die Empfehlungen und füllt jede vorhandene Hülle.
 *
 * ⚠️ Der Kunde schreibt `intent=complementary` vor — die in *Search &
 * Discovery* gepflegten ergänzenden Produkte, nicht „ähnliche". Sind dort
 * keine gepflegt, kommt eine leere Liste; dann bleibt die Zeile versteckt,
 * statt Beliebiges anzubieten.
 */
async function fillUpsell() {
  queued = false;

  for (const box of document.querySelectorAll(UPSELL)) {
    const row = box.querySelector('[data-cd-upsell-row]');
    const productId = box.dataset.cdUpsell;
    if (!row || !productId) continue;

    // Idempotenz gegen Mehrfachabrufe. Nach einem Morph fehlt das Attribut
    // wieder — der Server liefert es nicht mit —, der Marker setzt sich also
    // von selbst zurück.
    if (box.dataset.cdUpsellFilled === productId) continue;

    const exclude = new Set((box.dataset.cdUpsellExclude || '').split(',').filter(Boolean));

    pending?.abort();
    pending = new AbortController();

    try {
      // ⚠️ Die Route kommt aus Liquid, nicht hartcodiert. `routes.
      // product_recommendations_url` trägt das Markt- und Sprachpräfix —
      // ein festes `/recommendations/...` ginge im Primärmarkt gut und in
      // `/fr/…` oder `/en-nl/…` ins Leere. So macht es Horizon auch
      // (`sections/product-recommendations.liquid:39`).
      const base = box.dataset.cdUpsellUrl || '/recommendations/products';
      const url = `${base}.json?product_id=${encodeURIComponent(productId)}&limit=${LIMIT}&intent=complementary`;
      const res = await fetch(url, { signal: pending.signal });
      if (!res.ok) continue;
      const data = await res.json();

      const cards = (data.products || [])
        .filter((p) => !exclude.has(String(p.id)))
        .map((p) => card(p, box))
        .filter(Boolean);

      if (!cards.length) {
        box.hidden = true;
        continue;
      }

      row.replaceChildren(...cards);
      box.dataset.cdUpsellFilled = productId;
      box.hidden = false;
    } catch (err) {
      // Ein fehlgeschlagener Abruf darf den Korb nicht beschädigen. Die Zeile
      // bleibt weg — sie ist eine Zugabe, kein Bedienelement.
      if (err?.name !== 'AbortError') box.hidden = true;
    }
  }
}

/**
 * Eine Empfehlungskarte.
 *
 * ⚠️ Gibt `null` zurück, wenn es keine kaufbare Variante gibt. Ein `hidden` am
 * Element reichte NICHT: `.cd-cart-upsell__card { display: flex }` in
 * `cd-chrome.css` schlägt die UA-Regel `[hidden] { display: none }`, weil
 * Autoren-CSS gewinnt. Sichtbar bliebe eine leere umrandete Box.
 *
 * @param {any} product
 * @param {HTMLElement} box
 * @returns {HTMLElement | null}
 */
function card(product, box) {
  const variant = product.variants?.find((v) => v.available);
  if (!variant) return null;

  const el = document.createElement('form');
  el.className = 'cd-cart-upsell__card';
  el.method = 'post';
  el.action = box.dataset.cdUpsellAction || '/cart/add';

  const id = document.createElement('input');
  id.type = 'hidden';
  id.name = 'id';
  id.value = String(variant.id);

  const media = document.createElement('span');
  media.className = 'cd-cart-upsell__media';
  if (product.featured_image) {
    const img = document.createElement('img');
    // Dargestellt werden 40×48 — die Originaldatei dafür zu laden wäre
    // Verschwendung. Shopifys CDN skaliert über `width`.
    const sep = product.featured_image.includes('?') ? '&' : '?';
    img.src = `${product.featured_image}${sep}width=96`;
    img.alt = '';
    img.loading = 'lazy';
    img.width = 40;
    img.height = 48;
    media.appendChild(img);
  }

  const text = document.createElement('span');
  text.className = 'cd-cart-upsell__text';
  const title = document.createElement('b');
  title.textContent = product.title;
  const price = document.createElement('span');
  // ⚠️ Nicht `Intl` — das ignoriert Shopifys `money_format` (Symbolposition,
  // Trennzeichen). Das Theme bringt die Formatierung mit; Projektregel 4 sagt,
  // vorhandene Bausteine zu benutzen.
  // ⚠️ Drei Argumente, nicht zwei: Ohne `currency` kennt `formatMoney` die
  // Nachkommastellen der Währung nicht (`money-formatting.js:171`).
  price.textContent = formatMoney(
    variant.price,
    box.dataset.cdUpsellMoneyFormat,
    box.dataset.cdUpsellCurrency
  );
  text.append(title, price);

  const submit = document.createElement('button');
  submit.type = 'submit';
  submit.className = 'cd-cart-upsell__add';
  submit.textContent = '+';
  // Der sichtbare Text ist nur ein Pluszeichen — ohne Beschriftung liest ein
  // Screenreader „Plus, Schaltfläche" und weiss nicht, wozu. Kein
  // hartcodierter Fallback: Kernregel 1 lässt keine Ausnahme zu.
  const label = window.cdCartStrings?.upsellAdd;
  if (label) submit.setAttribute('aria-label', label.replace(/\{\{\s*title\s*\}\}/, product.title));

  el.append(id, media, text, submit);
  el.addEventListener('submit', onCardSubmit);
  return el;
}

/**
 * ⚠️ Ohne diesen Abfänger ist die Karte ein klassischer POST: Der Drawer
 * schliesst, die Seite lädt neu, der Besucher landet auf `/cart`. Horizons
 * `product-form.js` fängt sie NICHT ab — dessen Submit-Behandlung hängt an der
 * deklarativen Bindung `on:submit="/handleSubmit"` innerhalb einer
 * `product-form-component`, und die gibt es hier nicht.
 *
 * Das `<form>` bleibt trotzdem ein Formular: Schlägt der Abruf fehl, greift
 * der native Weg als Rückfallebene.
 *
 * @param {SubmitEvent} event
 */
async function onCardSubmit(event) {
  event.preventDefault();
  const form = /** @type {HTMLFormElement} */ (event.currentTarget);
  const button = form.querySelector('button[type="submit"]');
  if (button) button.disabled = true;

  try {
    const body = new FormData(form);
    // Ohne `sections` liefert Shopify nur den Korb zurück, und der Drawer
    // müsste ein zweites Mal nachladen.
    document.querySelectorAll('cart-items-component[data-section-id]').forEach((el) => {
      body.append('sections', el.getAttribute('data-section-id') ?? '');
    });

    const res = await fetch(form.action, { method: 'POST', body });
    if (!res.ok) throw new Error(String(res.status));

    // Horizons eigene Komponenten hören darauf und aktualisieren sich selbst.
    document.dispatchEvent(new Event('shopify:cart:lines-update', { bubbles: true }));
    if (window.cdCartStrings?.added) toast(window.cdCartStrings.added);
    queueFill();
  } catch {
    // Lieber ein Seitenwechsel als ein Knopf, der nichts tut.
    form.submit();
  } finally {
    if (button) button.disabled = false;
  }
}

/* ----------------------------------------------------------------- Init -- */

function queueFill() {
  if (queued) return;
  queued = true;
  requestAnimationFrame(fillUpsell);
}

/**
 * Lieferland wechseln.
 *
 * `{% form 'localization' %}` ist der Markets-Country-Selector — genau das, was
 * der Kunde vorschreibt („marktkorrekte Preise/Varianten"). Abgeschickt wird er
 * hier, weil ein `<select>` von sich aus nichts sendet.
 *
 * ⚠️ Der Wechsel lädt die Seite neu; das ist bei Shopify Markets unvermeidlich
 * und bei Horizon genauso. Der Korb liegt serverseitig und überlebt ihn — der
 * Drawer ist danach allerdings zu.
 *
 * Delegiert am `document`, nicht am Feld: Der Drawer wird bei jeder
 * Korb-Änderung gemorpht, ein direkt gebundener Zuhörer wäre danach weg.
 */
function bindCountrySelect() {
  document.addEventListener('change', (event) => {
    const select = event.target;
    if (!(select instanceof HTMLSelectElement)) return;
    if (!select.hasAttribute('data-cd-country-select')) return;
    select.form?.submit();
  });
}

/**
 * Höchstens EIN Panel offen — die Chip-Reihe im Warenkorb.
 *
 * Das ist eine ausdrückliche Kundenanweisung („es ist höchstens EIN Panel
 * offen, der Platz gehört den Produkten", DEV-Hinweis 09.08.) und zugleich
 * technisch nötig: Die Panels liegen alle in Grid-Zeile 2 über die volle
 * Breite — zwei gleichzeitig offene würden einander überlagern.
 *
 * ⚠️ Nicht über das native `name`-Attribut an `<details>` gelöst. Das gäbe es
 * (exklusives Akkordeon seit 2024), aber Horizons Rabattblock trägt es nicht,
 * und es nachzurüsten hiesse, eine Kerndatei zu ändern.
 *
 * Delegiert am `document`: Der Drawer wird bei jeder Korb-Änderung gemorpht,
 * ein direkt gebundener Zuhörer wäre danach weg.
 */
function bindExclusiveChips() {
  document.addEventListener('toggle', (event) => {
    const details = event.target;
    if (!(details instanceof HTMLDetailsElement) || !details.open) return;

    const row = details.closest('.cart-actions');
    if (!row) return;

    for (const other of row.querySelectorAll('details[open]')) {
      if (other !== details) other.open = false;
    }
  }, true); // capture: `toggle` blubbert nicht
}

function init() {
  fillUpsell();
  bindCountrySelect();
  bindExclusiveChips();

  // ⚠️ Die Ereignisse heissen `shopify:cart:*`, NICHT `cart:*`
  // (`assets/standard-events.d.ts:572-576`). Ein Zuhörer auf dem falschen
  // Namen feuert nie, und zwar lautlos.
  document.addEventListener('shopify:cart:lines-update', (event) => {
    const promise = /** @type {any} */ (event).promise;
    Promise.resolve(promise)
      .then((result) => {
        // ⚠️ Falle 1: `source` liegt hier, nicht an `event.detail`.
        // `didError` muss mit geprüft werden — bei einem Fehlschlag setzt
        // `product-form.js:484` dieselbe `source`.
        const detail = result?.detail;
        if (detail?.source === 'product-form-component' && !detail.didError && window.cdCartStrings?.added) {
          toast(window.cdCartStrings.added);
        }
      })
      .catch(() => {})
      .finally(queueFill);
  });

  // ⚠️ Falle 4: Beim ersten Artikel in einen leeren Korb existiert die Hülle
  // noch gar nicht — `cd-cart-extras` steht im `else`-Zweig von `cart.empty?`.
  // Und diesen Fall morpht Horizon über `startViewTransition`, also mehrere
  // Frames später. Ein rAF allein kommt zu früh; der Beobachter fängt es auf.
  const drawer = document.getElementById('cart-drawer');
  if (drawer && !watcher) {
    watcher = new MutationObserver(queueFill);
    watcher.observe(drawer, { childList: true, subtree: true });
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
